import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import {
  updateBrandVoice,
  uploadLogo,
  removeLogo,
  FILE_URL,
} from "@/lib/api";
import { Sparkle, Upload, Trash, ImageSquare } from "@phosphor-icons/react";
import { toast, Toaster } from "sonner";

export default function Settings() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [voice, setVoice] = useState({
    text: "",
    tone: "bold",
    formality: "professional",
    emoji_ok: false,
  });
  const [savingVoice, setSavingVoice] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.brand_voice) {
      setVoice({
        text: user.brand_voice.text || "",
        tone: user.brand_voice.tone || "bold",
        formality: user.brand_voice.formality || "professional",
        emoji_ok: !!user.brand_voice.emoji_ok,
      });
    }
  }, [user]);

  const saveVoice = async () => {
    setSavingVoice(true);
    try {
      await updateBrandVoice(voice);
      await refresh();
      toast.success("Brand voice saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingVoice(false);
    }
  };

  const handleLogoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800_000) {
      toast.error("Logo must be < 800KB");
      return;
    }
    setLogoBusy(true);
    try {
      await uploadLogo(f);
      await refresh();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Logo upload failed");
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleLogoRemove = async () => {
    setLogoBusy(true);
    try {
      await removeLogo();
      await refresh();
      toast.success("Logo removed");
    } catch {
      toast.error("Remove failed");
    } finally {
      setLogoBusy(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="h-10 w-10 rounded-full border-2 border-[#C9A961] border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] flex flex-col">
      <Navbar />
      <Toaster theme="dark" position="top-right" />
      <div className="flex-1">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-3">
              Settings
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter leading-none">
              Make every offer{" "}
              <span className="font-black">sound like you</span>.
            </h1>
            <p className="mt-4 text-[#9AA5B5] max-w-2xl">
              Tune the brand voice and add a logo. Both get applied to every
              new Irresistible Offer you forge.
            </p>
          </motion.div>

          {/* Brand voice */}
          <section
            className="mt-12 bg-[#0F1F38] border border-white/5 rounded-lg p-8 md:p-10"
            data-testid="brand-voice-section"
          >
            <div className="flex items-center gap-2 mb-6">
              <Sparkle weight="fill" className="h-5 w-5 text-[#C9A961]" />
              <h2 className="font-display text-2xl font-bold tracking-tight">
                Brand Voice
              </h2>
            </div>

            <div className="grid grid-cols-12 gap-6">
              <div className="col-span-12 md:col-span-6">
                <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961]">
                  Tone
                </Label>
                <Select
                  value={voice.tone}
                  onValueChange={(v) => setVoice((s) => ({ ...s, tone: v }))}
                >
                  <SelectTrigger
                    className="mt-2 bg-[#0A1628] border-white/15 text-[#F5F1E8]"
                    data-testid="voice-tone-trigger"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1F38] border-white/15 text-[#F5F1E8]">
                    <SelectItem value="bold">Bold &amp; direct</SelectItem>
                    <SelectItem value="luxury">Luxury / premium</SelectItem>
                    <SelectItem value="playful">Playful</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-12 md:col-span-6">
                <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#C9A961]">
                  Formality
                </Label>
                <Select
                  value={voice.formality}
                  onValueChange={(v) => setVoice((s) => ({ ...s, formality: v }))}
                >
                  <SelectTrigger
                    className="mt-2 bg-[#0A1628] border-white/15 text-[#F5F1E8]"
                    data-testid="voice-formality-trigger"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F1F38] border-white/15 text-[#F5F1E8]">
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-12 flex items-center justify-between bg-[#0A1628] border border-white/10 rounded-sm px-4 py-3">
                <div>
                  <Label className="text-sm text-[#F5F1E8]">Allow emoji</Label>
                  <p className="text-xs text-[#9AA5B5]">
                    Sprinkle relevant emoji into copy (off by default).
                  </p>
                </div>
                <Switch
                  checked={voice.emoji_ok}
                  onCheckedChange={(v) => setVoice((s) => ({ ...s, emoji_ok: v }))}
                  data-testid="voice-emoji-switch"
                />
              </div>

              <div className="col-span-12">
                <Label className="text-xs tracking-[0.25em] uppercase font-bold text-[#9AA5B5]">
                  Brand voice notes (optional)
                </Label>
                <Textarea
                  value={voice.text}
                  onChange={(e) => setVoice((s) => ({ ...s, text: e.target.value }))}
                  placeholder="e.g. We talk like a brutally honest mentor. Avoid jargon. Always reference real numbers. Don't say 'leverage'."
                  rows={4}
                  className="mt-2 bg-[#0A1628] border-white/15 focus-visible:border-[#C9A961] focus-visible:ring-0 text-[#F5F1E8] placeholder:text-white/40 resize-none rounded-sm"
                  data-testid="voice-notes-textarea"
                />
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <Button
                onClick={saveVoice}
                disabled={savingVoice}
                className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm px-6"
                data-testid="voice-save-btn"
              >
                {savingVoice ? "Saving…" : "Save brand voice"}
              </Button>
            </div>
          </section>

          {/* Logo */}
          <section
            className="mt-8 bg-[#0F1F38] border border-white/5 rounded-lg p-8 md:p-10"
            data-testid="logo-section"
          >
            <div className="flex items-center gap-2 mb-6">
              <ImageSquare weight="duotone" className="h-5 w-5 text-[#C9A961]" />
              <h2 className="font-display text-2xl font-bold tracking-tight">
                PDF Logo
              </h2>
            </div>
            <p className="text-sm text-[#9AA5B5] mb-6">
              Optional. Embedded in the top-left of every PDF you export. PNG,
              JPG, or WebP under 800KB.
            </p>

            <div className="flex items-center gap-6 flex-wrap">
              <div
                className="h-24 w-40 bg-[#0A1628] border border-white/10 rounded-sm flex items-center justify-center overflow-hidden"
                data-testid="logo-preview"
              >
                {user.logo_path ? (
                  <img
                    src={FILE_URL(user.logo_path)}
                    alt="Logo"
                    className="max-h-20 max-w-36 object-contain"
                  />
                ) : (
                  <span className="text-xs text-[#808A9B]">No logo</span>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  onChange={handleLogoChange}
                  className="hidden"
                  data-testid="logo-file-input"
                />
                <Button
                  onClick={() => fileRef.current?.click()}
                  disabled={logoBusy}
                  className="bg-[#C9A961] text-[#0A1628] font-bold hover:bg-[#E0B64B] rounded-sm"
                  data-testid="logo-upload-btn"
                >
                  <Upload weight="bold" className="mr-2 h-4 w-4" />
                  {user.logo_path ? "Replace logo" : "Upload logo"}
                </Button>
                {user.logo_path && (
                  <Button
                    onClick={handleLogoRemove}
                    disabled={logoBusy}
                    variant="outline"
                    className="bg-transparent border-white/15 text-[#F5F1E8] hover:bg-white/5 hover:border-[#ef4444] hover:text-[#ef4444] rounded-sm"
                    data-testid="logo-remove-btn"
                  >
                    <Trash weight="bold" className="mr-2 h-4 w-4" /> Remove
                  </Button>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
